import React from "react";
import { RouteComponentProps } from "react-router";
import { Spin, message, Button, Row, Col } from "antd";
import { AxiosResponse } from "axios";
import { FormInstance } from "antd/lib/form";
import * as Sentry from "@sentry/browser";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  Provider,
  SelectOptionsInterface,
  FormFieldConfig,
} from "../helpers/shared-interfaces";
import {
  request,
  responseHandler,
  generateDisplayErrorMessage,
  cleanStringArray,
} from "../helpers/utilities";
import { GET_PROVIDER, UPDATE_PROVIDER, LOAN_DOCUMENTS } from "../gql/loans";

interface State {
  isLoading: boolean;
  provider: Provider | null;
  isSubmitting: boolean;
  loanDocuments: SelectOptionsInterface[];
}

interface RouteParams {
  id: string;
}

export default class EditProvider extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  formRef = React.createRef<FormInstance>();

  state: State = {
    isLoading: false,
    provider: null,
    isSubmitting: false,
    loanDocuments: [],
  };

  componentDidMount(): void {
    this.getProviderData();
    this.fetchLoanDocuments();
  }

  getProviderData = (): void => {
    const { id } = this.props.match.params;
    this.setState({ isLoading: true });

    request({
      query: GET_PROVIDER,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<any>): void => {
        this.setState({ isLoading: false });
        if (!data.errors) {
          this.setState({ provider: data.data.getProvider });
        } else {
          message.error("Failed to get provider data");
        }
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        message.error("Failed to get provider data");
        Sentry.captureException(error);
      });
  };

  fetchLoanDocuments = async (): Promise<void> => {
    try {
      const response: AxiosResponse = await request({
        query: LOAN_DOCUMENTS,
      });

      const { data } = response.data;

      if (data?.loanDocuments) {
        const documents = data.loanDocuments.map((doc: any) => ({
          id: doc.id,
          name: doc.name,
          value: doc.name,
        }));
        this.setState({ loanDocuments: documents });
      }
    } catch (error) {
      message.error(`Failed to fetch loan documents - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  handleCancel = (): void => {
    const { id } = this.props.match.params;
    this.props.history.push(`/providers/${id}`);
  };

  updateProvider = async (): Promise<void> => {
    interface Response {
      provider: Provider;
    }

    if (!this.formRef.current) return;

    this.setState({ isSubmitting: true });

    try {
      const data = await this.formRef.current.validateFields([
        "name",
        "contactEmail",
        "ccEmails",
        "securedRequiredDocuments",
        "semiSecuredRequiredDocuments",
        "unsecuredRequiredDocuments",
        "promo100RequiredDocuments",
        "termsAndConditions",
      ]);

      const variables = {
        id: this.state.provider?.id,
        name: data.name,
        contactEmail: data.contactEmail || null,
        ccEmails: cleanStringArray(data.ccEmails),
        securedRequiredDocuments: data.securedRequiredDocuments || [],
        semiSecuredRequiredDocuments: data.semiSecuredRequiredDocuments || [],
        unsecuredRequiredDocuments: data.unsecuredRequiredDocuments || [],
        promo100RequiredDocuments: data.promo100RequiredDocuments || [],
        termsAndConditions: data.termsAndConditions || null,
      };

      const response: AxiosResponse = await request({
        query: UPDATE_PROVIDER,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateProvider"
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        message.success("Provider updated successfully!");
        this.props.history.push(`/providers/${mutation.provider.id}`);
      }
    } catch (error) {
      message.error(`Failed to update provider - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render(): JSX.Element {
    const { isLoading, provider, isSubmitting, loanDocuments } = this.state;

    if (isLoading || !provider) {
      return (
        <Card header="Edit Provider">
          <Spin spinning={true}>
            <div style={{ minHeight: 200 }} />
          </Spin>
        </Card>
      );
    }

    const fields: FormFieldConfig[] = [
      {
        id: "name",
        name: "name",
        inputType: "string",
        required: true,
        placeholder: "Enter provider name",
        rule: "string",
        label: "Provider Name",
        initialValue: provider.name,
        colConfig: { span: 7 },
      },
      {
        id: "contactEmail",
        name: "contactEmail",
        inputType: "string",
        required: false,
        placeholder: "Enter contact email",
        rule: "string",
        label: "Contact Email",
        initialValue: provider.contactEmail,
        colConfig: { span: 7, offset: 1 },
      },
      {
        id: "ccEmails",
        name: "ccEmails",
        inputType: "emailArray",
        required: false,
        placeholder: "Enter CC email",
        rule: "array",
        label: "CC Emails",
        initialValue:
          provider.ccEmails && provider.ccEmails.length > 0
            ? provider.ccEmails
            : [""],
        colConfig: { span: 15 },
        elementStyle: { marginBottom: "8px", width: "100%" },
      },
      {
        id: "securedRequiredDocuments",
        name: "securedRequiredDocuments",
        inputType: "multipleSelect",
        required: false,
        placeholder: "Select required documents for secured loans",
        rule: "string",
        label: "Secured Required Documents",
        selectOptions: loanDocuments,
        initialValue: provider.securedRequiredDocuments || [],
        colConfig: { span: 11 },
        elementStyle: { marginBottom: "20px" },
      },
      {
        id: "semiSecuredRequiredDocuments",
        name: "semiSecuredRequiredDocuments",
        inputType: "multipleSelect",
        required: false,
        placeholder: "Select required documents for semi-secured loans",
        rule: "string",
        label: "Semi-Secured Required Documents",
        selectOptions: loanDocuments,
        initialValue: provider.semiSecuredRequiredDocuments || [],
        colConfig: { span: 11, offset: 1 },
        elementStyle: { marginBottom: "20px" },
      },
      {
        id: "unsecuredRequiredDocuments",
        name: "unsecuredRequiredDocuments",
        inputType: "multipleSelect",
        required: false,
        placeholder: "Select required documents for unsecured loans",
        rule: "string",
        label: "Unsecured Required Documents",
        selectOptions: loanDocuments,
        initialValue: provider.unsecuredRequiredDocuments || [],
        colConfig: { span: 11 },
        elementStyle: { marginBottom: "20px" },
      },
      {
        id: "promo100RequiredDocuments",
        name: "promo100RequiredDocuments",
        inputType: "multipleSelect",
        required: false,
        placeholder: "Select required documents for promo 100 loans",
        rule: "string",
        label: "Promo 100 Required Documents",
        selectOptions: loanDocuments,
        initialValue: provider.promo100RequiredDocuments || [],
        colConfig: { span: 11, offset: 1 },
        elementStyle: { marginBottom: "20px" },
      },
      {
        id: "termsAndConditions",
        name: "termsAndConditions",
        inputType: "textArea",
        required: false,
        autoSize: { minRows: 12, maxRows: 30 },
        rule: "string",
        label: "Terms & Conditions",
        initialValue: provider.termsAndConditions || "",
        colConfig: { span: 23 },
        elementStyle: { marginBottom: "20px" },
      },
    ];

    return (
      <Card header="Edit Provider">
        <Spin spinning={isSubmitting}>
          <BaseForm
            layout="vertical"
            reference={this.formRef}
            fields={fields}
            fieldsToHide={[]}
          />
          <Row gutter={16} style={{ marginTop: 32 }}>
            <Col>
              <Button
                type="primary"
                className="base-button"
                onClick={() => this.updateProvider()}
                loading={isSubmitting}
              >
                Update Provider
              </Button>
            </Col>
            <Col>
              <Button onClick={this.handleCancel} style={{ height: "33px" }}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Spin>
      </Card>
    );
  }
}
